import { IComparisonRow, ISupportTier } from '@/interfaces/supportPlan';

export const SUPPORT_COMPARISON_ROWS: IComparisonRow[] = [
  {
    label: { en: 'Your contact', zh: '聯繫對象' },
    values: {
      standard: { en: 'Support team', zh: '支援團隊' },
      priority: { en: 'Priority queue', zh: '優先佇列' },
      enterprise: { en: 'Named manager', zh: '專屬經理' },
    },
  },
  {
    label: { en: 'Email & in-app chat', zh: '電子郵件與應用內聊天' },
    values: { standard: true, priority: true, enterprise: true },
  },
  {
    label: { en: 'Help Center', zh: '說明中心' },
    values: { standard: true, priority: true, enterprise: true },
  },
  {
    label: { en: 'Hands-on 1-on-1 onboarding', zh: '一對一專人上線輔導' },
    values: { standard: false, priority: true, enterprise: true },
  },
  {
    label: { en: 'Early access to beta features', zh: '搶先體驗新功能' },
    values: { standard: false, priority: true, enterprise: true },
  },
  {
    label: {
      en: 'Check-ins / Business Reviews',
      zh: '定期聯繫／業務檢視會議',
    },
    values: {
      standard: false,
      priority: { en: 'Check-ins', zh: '定期聯繫' },
      enterprise: { en: 'Business Reviews', zh: '業務檢視會議' },
    },
  },
  {
    label: { en: 'Custom integration consulting', zh: '客製整合諮詢' },
    values: { standard: false, priority: false, enterprise: true },
  },
  {
    label: {
      en: 'Security & compliance review',
      zh: '資安與合規審查',
    },
    values: { standard: false, priority: false, enterprise: true },
  },
  {
    label: { en: 'Custom training programs', zh: '客製化教育訓練' },
    values: { standard: false, priority: false, enterprise: true },
  },
  {
    label: {
      en: 'Product roadmap input',
      zh: '產品藍圖建議參與',
    },
    values: { standard: false, priority: false, enterprise: true },
  },
  {
    label: { en: 'Negotiable terms', zh: '可商議合約條款' },
    values: { standard: false, priority: false, enterprise: true },
  },
];

export const SUPPORT_TIERS: ISupportTier[] = [
  {
    id: 'standard',
    name: { en: 'Standard', zh: '標準支援' },
    tagline: {
      en: 'Everything you need to run your business — included with every plan.',
      zh: '營運所需的完整支援——所有方案皆包含。',
    },
    pricePerLocation: 'included',
    cta: { en: 'Included with every plan', zh: '所有方案皆包含' },
    ctaHref: lang => `/${lang}/pricing/`,
    features: [
      {
        en: 'Email and in-app chat support',
        zh: '電子郵件與應用內即時通訊支援',
      },
      {
        en: 'Best-effort response during business hours',
        zh: '上班時間盡速回覆',
      },
      {
        en: 'Full access to the Help Center and changelog',
        zh: '完整使用說明中心與更新紀錄',
      },
      {
        en: 'Group onboarding webinars',
        zh: '團體上線教學課程',
      },
      {
        en: 'Self-serve data import tools',
        zh: '自助資料匯入工具',
      },
      {
        en: 'Community forum and shared best practices',
        zh: '社群論壇與最佳實踐分享',
      },
    ],
  },
  {
    id: 'priority',
    name: { en: 'Priority', zh: '優先支援' },
    tagline: {
      en: 'Faster responses, hands-on onboarding, and a queue you skip to the front of.',
      zh: '更快速的回覆、專人協助上線，您的問題優先處理。',
    },
    pricePerLocation: { usd: 13, twd: 390 },
    pricePerLocationAnnual: { usd: 130, twd: 3900 },
    cta: { en: 'Upgrade to Priority', zh: '升級至優先支援' },
    ctaHref: lang => `/${lang}/contact/?topic=support-priority`,
    highlight: true,
    features: [
      {
        en: 'Everything in Standard',
        zh: '包含標準支援所有項目',
      },
      {
        en: 'Faster response times — your tickets jump the queue',
        zh: '更快速的回覆時間——您的問題優先進入處理佇列',
      },
      {
        en: 'Phone support during extended business hours',
        zh: '延長時段提供電話支援',
      },
      {
        en: 'Hands-on 1-on-1 onboarding session',
        zh: '一對一專人上線輔導',
      },
      {
        en: 'Assisted data migration from other platforms',
        zh: '協助從其他平台匯入資料',
      },
      {
        en: 'Quarterly check-in calls with your success contact',
        zh: '每季與專屬客戶成功窗口電話聯繫',
      },
      {
        en: 'Early access to beta features',
        zh: '搶先體驗新功能 Beta',
      },
      {
        en: 'EN and 繁體中文 native support',
        zh: '英文與繁體中文母語支援',
      },
    ],
  },
  {
    id: 'enterprise',
    name: { en: 'Enterprise', zh: '企業支援' },
    tagline: {
      en: 'A named account manager, peak-season coverage, and a partnership built around your business.',
      zh: '專屬客戶經理、旺季營運支援，為您的事業量身打造的夥伴關係。',
    },
    pricePerLocation: 'custom',
    cta: { en: 'Talk to sales', zh: '聯繫業務團隊' },
    ctaHref: lang => `/${lang}/contact/?topic=support-enterprise`,
    features: [
      {
        en: 'Everything in Priority',
        zh: '包含優先支援所有項目',
      },
      {
        en: 'Dedicated Account Manager — one named contact who knows your business',
        zh: '專屬客戶經理——熟悉您事業的單一窗口',
      },
      {
        en: 'Peak-season operations support during your busiest periods',
        zh: '旺季期間提供營運支援',
      },
      {
        en: 'Quarterly Business Reviews with strategic recommendations',
        zh: '每季業務檢視會議與策略建議',
      },
      {
        en: 'Custom integration and API consulting hours',
        zh: '客製整合與 API 諮詢時數',
      },
      {
        en: 'Security and compliance review support (DPA, custom MSA)',
        zh: '資安與合規審查支援（資料處理協議、客製主合約）',
      },
      {
        en: 'Custom training programs for your team',
        zh: '為您的團隊量身打造的教育訓練',
      },
      {
        en: 'Influence the product roadmap — quarterly sessions with our product team',
        zh: '影響產品藍圖——每季與產品團隊會議',
      },
      {
        en: 'Multi-location and franchise governance support',
        zh: '多分店與加盟體系治理支援',
      },
      {
        en: 'Custom terms and SLA negotiable',
        zh: '可商議客製合約條款與服務水準',
      },
    ],
  },
];
